import Head from 'next/head'
import React from 'react'
import clsx from 'clsx'

import { Text } from '@wartek-id/text'
import { Button } from '@wartek-id/button'
import { Icon } from '@wartek-id/icon'

import TopicFilter from '../components/TopicFilter'
import Card from '../partials/Topic/TopicCard'
import TopicPlaceholder from '../partials/Topic/TopicPlaceholder'
import GenericList from '../components/GenericList'
import { EmptyState, EmptyStateAction } from 'components/EmptyState'
import TabbedNav from '../components/TabbedNav'
import { determineBadge, shouldHideNav } from '../helper'
import { useSafeRouter } from 'hooks/useSafeRouter'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { useAPITopicList } from 'api/microlearning/useAPITopicList'
import { useSETrackerEvent } from '../hooks/useSETrackEvent'

import type { TTopicList } from 'types/microlearning'

const TopicListRevamp = ({ isDesktop = false, currentId = null }) => {
  const { SETrackTopicList, SETrackChooseOrder } = useSETrackerEvent()
  const { safeReplace } = useSafeRouter()
  const useFilter = useLocalStorage('topic-sort-filter', 'recommendation')
  const filterValue = useFilter[0] || 'recommendation'

  const [isSheetOpen, setIsSheetOpen] = React.useState(false)

  const { data: topicListData, status: topicListStatus } = useAPITopicList({
    sort: filterValue,
  })

  const STATE_FILTER = {
    recommendation: 'Rekomendasi',
    newest: 'Paling Baru',
    oldest: 'Paling lama',
    'almost-done': 'Hampir selesai',
    'just-started': 'Baru mulai',
    title: 'Judul A-Z',
  }

  const handleTracker = (item: TTopicList) => {
    SETrackTopicList({
      action_type: 'choose_topik',
      action_filter: filterValue,
      topic_id: item.id,
    })
  }

  const selectFilter = (value: string) => {
    useFilter[1](value)
    setIsSheetOpen(false)

    SETrackChooseOrder({
      action_type: 'choose_order',
      action_filter: value,
    })
  }

  const RenderTopicList = () => (
    <GenericList
      data={topicListData?.data || []}
      dataState={topicListStatus}
      listKey={({ id }) => id}
      errorComponent={
        <EmptyState
          title="Data gagal dimuat"
          description="Ada kesalahan pada sistem. Silakan coba beberapa saat lagi."
        >
          <EmptyStateAction onClick={() => window.location.reload()}>
            Muat Ulang
          </EmptyStateAction>
        </EmptyState>
      }
      emptyComponent={
        <EmptyState
          title="Konten belum tersedia"
          description="Belum ada topik yang dapat dipelajari saat ini; silakan coba lagi nanti."
        />
      }
      loadingComponent={<TopicPlaceholder amount={4} />}
      renderItem={(item) => (
        <div onClick={() => handleTracker(item)}>
          <Card
            topicId={item.id}
            isFetchingProgress={topicListStatus !== 'success'}
            name={item.name}
            certifiable={item.certifiable}
            totalModule={item.totalModule}
            description={item.description}
            href={`/${
              process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
              'pelatihan-mandiri'
            }/topik/${item.id}?filter=${filterValue}`}
            image={item.headerImage}
            badge={determineBadge(item) as any}
            active={String(item.id) === currentId}
          />
        </div>
      )}
      itemClassName="mb-2"
    />
  )

  if (isDesktop) {
    if (!currentId) {
      const firstTopic = topicListData?.data?.[0]

      firstTopic?.id &&
        safeReplace(`/pelatihan-mandiri/topik/${firstTopic?.id}`)
    }

    return (
      <>
        <Head>
          <title>Pelatihan Mandiri | Daftar Topik</title>
        </Head>
        <div className="mt-[80px] px-4">
          <Text variant="display-sm" className="mb-4">
            Daftar Topik
          </Text>

          <RenderTopicList />
        </div>
      </>
    )
  }

  return (
    <>
      <Head>
        <title>Pelatihan Mandiri | Daftar Topik</title>
      </Head>
      {!shouldHideNav() && <TabbedNav activeMenu="topik" />}
      <div
        className="bg-gray-10"
        style={{
          minHeight: 'calc(100vh - 3.5rem)',
        }}
      >
        <div>
          <div
            className={clsx(shouldHideNav() ? 'pt-3' : 'pt-0') + ' relative'}
          >
            <div className="p-3 bg-white sticky top-[90px] z-10 mb-12 drop-shadow-md">
              <Button
                color="white"
                fullWidth
                onClick={() => setIsSheetOpen(true)}
              >
                <div className="flex items-center">
                  <div className="mr-1">
                    <Icon>sort</Icon>
                  </div>
                  <div>
                    Urutkan:{' '}
                    <b className="text-blue-60">
                      {STATE_FILTER[filterValue] ||
                        STATE_FILTER['recommendation']}
                    </b>
                  </div>
                </div>
              </Button>
            </div>

            <TopicFilter
              isOpen={isSheetOpen}
              selected={filterValue}
              onClose={() => setIsSheetOpen(false)}
              onSelect={(value) => selectFilter(value)}
            />
            <div className="px-3 pb-5">
              <RenderTopicList />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default TopicListRevamp
